import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import Modal from '../components/Modal';
import PassCard from '../components/PassCard';
import { StatusBadge } from './Dashboard';
import { Plus, Search, DoorOpen, Printer, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const PASS_TYPES = [
  { value: 'official', label: 'Official' },
  { value: 'personal', label: 'Personal' },
];
const STATUSES = ['all', 'pending', 'approved', 'out', 'closed', 'rejected'];

export default function GatePasses() {
  const { user } = useAuth();
  const location = useLocation();
  const [passes, setPasses] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [openCreate, setOpenCreate] = useState(location.state?.openCreate === true);
  const [selected, setSelected] = useState(null);
  const [departments, setDepartments] = useState([]);

  const isMyPasses = user.role === 'admin' || user.role === 'hr' || user.role === 'supervisor';
  const load = async () => {
    const params = filter !== 'all' ? { status: filter } : {};
    if (isMyPasses) params.mine = 'true';
    const { data } = await api.get('/gate-passes', { params });
    setPasses(data);
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    api.get('/departments').then(({ data }) => setDepartments(data)).catch(() => {});
  }, []);

  const filtered = passes.filter((p) =>
    !q || p.employee_name.toLowerCase().includes(q.toLowerCase()) ||
    p.pass_id.toLowerCase().includes(q.toLowerCase()) ||
    (p.department || '').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="gate-passes-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Module 02</div>
          <h1 className="heading text-3xl font-black text-ink">
            {isMyPasses ? 'My Passes' : 'Gate Passes'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isMyPasses ? 'Your personal gate pass history.' : 'Request exits, track approvals, verify at gate.'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpenCreate(true)} data-testid="new-gate-pass-btn">
          <Plus size={16} /> Request Gate Pass
        </button>
      </header>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            className="input pl-9"
            placeholder="Search by name, pass ID, department…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border ${
                filter === s ? 'bg-ink text-white border-ink' : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
                <th className="px-4 py-3 overline">Pass ID</th>
                <th className="px-4 py-3 overline">Employee</th>
                <th className="px-4 py-3 overline">Department</th>
                <th className="px-4 py-3 overline">Type</th>
                <th className="px-4 py-3 overline">Reason</th>
                <th className="px-4 py-3 overline">Time Out / In</th>
                <th className="px-4 py-3 overline">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="passes-table-body">
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-zinc-400">No gate passes found.</td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-mono text-xs font-bold text-ink">{p.pass_id}</td>
                  <td className="px-4 py-3 font-semibold">{p.employee_name}</td>
                  <td className="px-4 py-3 text-zinc-600">{p.department}</td>
                  <td className="px-4 py-3">
                    <span className={p.pass_type === 'official' ? 'badge-info' : 'badge-muted'}>{p.pass_type}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 max-w-[200px] truncate">{p.reason}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {p.time_out && <div>OUT: {format(new Date(p.time_out), 'dd MMM HH:mm')}</div>}
                    {p.time_in && <div>IN: {format(new Date(p.time_in), 'dd MMM HH:mm')}</div>}
                    {!p.time_out && <div className="text-zinc-300">—</div>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={p.status} />
                    {p.approved_at && !['pending', 'cancelled'].includes(p.status) && (
                      <div className="text-xs text-zinc-400 mt-1">
                        {p.status === 'rejected' ? 'Rejected' : 'Approved'}: {format(new Date(p.approved_at), 'dd MMM yyyy HH:mm')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="btn-ghost p-1.5" onClick={() => setSelected(p)} data-testid={`view-${p.pass_id}`}>
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {openCreate && (
        <CreateGatePassModal
          onClose={() => setOpenCreate(false)}
          onCreated={(p) => { setOpenCreate(false); load(); setSelected(p); }}
          defaultUser={user}
          departments={departments}
        />
      )}
      {selected && <PassDetailModal pass={selected} onClose={() => setSelected(null)} onUpdated={() => { load(); setSelected(null); }} />}
    </div>
  );
}

function CreateGatePassModal({ onClose, onCreated, defaultUser, departments }) {
  const [form, setForm] = useState({
    employee_name: defaultUser.name || '',
    employee_id: defaultUser.employee_code || '',
    department: defaultUser.department || '',
    reason: '',
    pass_type: 'personal',
    will_return: true,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr('');
    try {
      const { data } = await api.post('/gate-passes', form);
      const isSelfApproved = defaultUser.role === 'admin';
      toast.success(isSelfApproved
        ? `Gate pass ${data.pass_id} approved · ready at the gate`
        : `Gate pass ${data.pass_id} requested · awaiting supervisor`);
      onCreated(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, e?.response?.data) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Request Gate Pass"
      subtitle="Gate Pass · Step 1/1"
      size="md"
      footer={
        <>
          <button className="btn-outline" onClick={onClose} data-testid="cancel-create">Cancel</button>
          <button type="submit" form="gp-form" className="btn-primary" disabled={loading} data-testid="submit-create">
            {loading ? 'Submitting…' : 'Submit for Approval'}
          </button>
        </>
      }
    >
      <form id="gp-form" onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Employee Name *</label>
            <input className="input" required value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} data-testid="gp-employee-name" />
          </div>
          <div>
            <label className="label">Employee Code</label>
            <input className="input" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="gp-employee-code" />
          </div>
        </div>
        <div>
          <label className="label">Department *</label>
          <select className="input" required value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="gp-department">
            <option value="">Select department</option>
            {departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Reason *</label>
          <textarea className="input" required rows={3} placeholder="e.g. personal work — bank visit" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="gp-reason" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Pass Type *</label>
            <div className="flex gap-2">
              {PASS_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setForm({ ...form, pass_type: t.value })}
                  data-testid={`gp-type-${t.value}`}
                  className={`flex-1 py-2 text-sm font-bold uppercase border rounded-sm ${
                    form.pass_type === t.value ? 'bg-primary text-white border-primary' : 'bg-white border-zinc-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">Will Return?</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm({ ...form, will_return: true })}
                data-testid="gp-return-yes"
                className={`flex-1 py-2 text-sm font-bold uppercase border rounded-sm ${form.will_return ? 'bg-green-600 text-white border-green-600' : 'bg-white border-zinc-300'}`}
              >Return</button>
              <button
                type="button"
                onClick={() => setForm({ ...form, will_return: false })}
                data-testid="gp-return-no"
                className={`flex-1 py-2 text-sm font-bold uppercase border rounded-sm ${!form.will_return ? 'bg-red-600 text-white border-red-600' : 'bg-white border-zinc-300'}`}
              >No Return</button>
            </div>
          </div>
        </div>
        {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
      </form>
    </Modal>
  );
}

function PassDetailModal({ pass, onClose, onUpdated }) {
  const printPass = () => window.print();
  return (
    <Modal open onClose={onClose} title={`Gate Pass · ${pass.pass_id}`} subtitle="Print & share with guard" size="lg"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Close</button>
          <button className="btn-secondary" onClick={printPass} data-testid="print-pass"><Printer size={16} /> Print</button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <StatusBadge status={pass.status} />
          {pass.approved_by_name && <span className="text-xs text-zinc-500">Approved by {pass.approved_by_name}</span>}
          {pass.rejected_reason && <span className="text-xs text-red-600">Rejected: {pass.rejected_reason}</span>}
        </div>
        <PassCard
          passId={pass.pass_id}
          passcode={pass.passcode}
          title={pass.employee_name}
          subtitle={`${pass.pass_type.toUpperCase()} GATE PASS`}
          lines={[
            { label: 'Department', value: pass.department },
            { label: 'Reason', value: pass.reason },
            { label: 'Requested', value: pass.created_at ? format(new Date(pass.created_at), 'dd MMM yyyy HH:mm') : '—' },
            ...(pass.approved_at && !['pending', 'cancelled'].includes(pass.status) ? [
              { label: pass.status === 'rejected' ? 'Rejected At' : 'Approved At', value: format(new Date(pass.approved_at), 'dd MMM yyyy HH:mm') },
            ] : []),
            { label: 'Return', value: pass.will_return ? 'Yes' : 'No Return' },
          ]}
          footer={<div className="text-xs text-zinc-500 text-right">Show this pass at the gate<br/>or dictate the 6-digit code.</div>}
        />
      </div>
    </Modal>
  );
}
